import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SaveButton from '@/components/SaveButton';
import { Button } from '@/components/ui/button';
import { 
  Briefcase, 
  MapPin, 
  Clock, 
  Building2, 
  Globe,
  DollarSign,
  Plane,
  ArrowLeft,
  ExternalLink,
  Mail,
  Calendar,
  Eye,
  Share2,
  CheckCircle,
  Edit,
  Trash2
} from 'lucide-react';
import { API, useAuth } from '@/App';

const JobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJob();
  }, [jobId]);

  const fetchJob = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${jobId}`);
      setJob(response.data);
    } catch (error) {
      toast.error('Job not found');
      navigate('/jobs');
    } finally {
      setLoading(false);
    }
  };

  const formatSalary = () => {
    if (!job?.salary_min && !job?.salary_max) return null;
    const currency = job.salary_currency || 'USD';
    const period = job.salary_period || 'year';
    
    let salary = '';
    if (job.salary_min && job.salary_max) {
      salary = `${currency} ${job.salary_min.toLocaleString()} - ${job.salary_max.toLocaleString()}`;
    } else if (job.salary_min) {
      salary = `From ${currency} ${job.salary_min.toLocaleString()}`;
    } else {
      salary = `Up to ${currency} ${job.salary_max.toLocaleString()}`;
    }
    
    return `${salary} per ${period}`;
  };

  const handleApply = () => {
    if (job.application_method === 'email') {
      window.location.href = `mailto:${job.application_email}?subject=Application for ${job.title}`;
    } else if (job.application_method === 'external_link' && job.application_url) {
      window.open(job.application_url, '_blank');
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${job.title} at ${job.company_name}`,
        text: `Check out this job opportunity: ${job.title}`,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const isOwner = user && job && (job.is_owner || job.posted_by === user.user_id || job.poster_user_id === user.user_id);
  const canEdit = (job && job.can_edit) || isOwner;

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this job listing? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/jobs/${jobId}`);
      toast.success('Job listing deleted');
      navigate('/jobs');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete job listing');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <div className="flex items-center justify-between mb-6">
            <Link to="/jobs" className="inline-flex items-center text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Jobs
            </Link>
            {isOwner && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate(`/jobs/${jobId}/edit`)} data-testid="edit-job-btn">
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button variant="destructive" size="sm" onClick={handleDelete} data-testid="delete-job-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </div>
            )}
          </div>

          {/* Owner Actions Banner */}
          {canEdit && (
            <div className="bg-primary/5 border border-primary/20 rounded-xl px-4 py-3 mb-6 flex items-center justify-between">
              <span className="text-sm font-medium text-primary">This is your listing</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => navigate(`/jobs/${jobId}/edit`)} data-testid="edit-job-banner-btn">
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="destructive" onClick={handleDelete} data-testid="delete-job-banner-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </div>
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Job Header */}
            <div className="bg-white rounded-3xl p-8 border-2 border-border mb-6">
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 bg-muted rounded-2xl flex items-center justify-center flex-shrink-0">
                  {job.company_logo ? (
                    <img src={job.company_logo} alt={job.company_name} className="w-full h-full object-cover rounded-2xl" />
                  ) : (
                    <Building2 className="w-10 h-10 text-muted-foreground" />
                  )}
                </div>

                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold mb-2">{job.title}</h1>
                  <p className="text-xl text-muted-foreground mb-4">{job.company_name}</p>

                  <div className="flex flex-wrap gap-3">
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm bg-muted font-medium">
                      <MapPin className="w-4 h-4" />
                      {job.city}, {job.country}
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm bg-blue-100 text-blue-700 font-medium capitalize">
                      <Globe className="w-4 h-4" />
                      {job.work_arrangement}
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm bg-muted font-medium capitalize">
                      <Clock className="w-4 h-4" />
                      {job.job_type.replace('-', ' ')}
                    </span>
                    {job.visa_sponsorship && (
                      <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm bg-accent/10 text-accent font-bold">
                        <Plane className="w-4 h-4" />
                        Visa Sponsorship
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Salary & Meta */}
              <div className="flex flex-wrap items-center gap-6 mt-6 pt-6 border-t border-border">
                {formatSalary() && (
                  <div className="flex items-center gap-2 text-green-600 font-bold text-lg">
                    <DollarSign className="w-5 h-5" />
                    {formatSalary()}
                  </div>
                )}
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  Posted {formatDate(job.created_at)}
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Eye className="w-4 h-4" />
                  {job.views} views
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-6">
                <Button onClick={handleApply} className="flex-1 h-12 rounded-full bg-primary font-bold">
                  {job.application_method === 'email' ? (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Apply via Email
                    </>
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Apply Now
                    </>
                  )}
                </Button>
                <SaveButton 
                  itemType="job" 
                  itemId={job.job_id} 
                  className="h-12 px-6 rounded-full"
                />
                <Button variant="outline" onClick={handleShare} className="h-12 px-6 rounded-full">
                  <Share2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Job Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Main Content */}
              <div className="md:col-span-2 space-y-6">
                <div className="bg-white rounded-2xl p-6 border-2 border-border">
                  <h2 className="text-xl font-bold mb-4">Job Description</h2>
                  <div className="prose prose-lg max-w-none text-muted-foreground whitespace-pre-wrap">
                    {job.description}
                  </div>
                </div>

                {job.requirements && (
                  <div className="bg-white rounded-2xl p-6 border-2 border-border">
                    <h2 className="text-xl font-bold mb-4">Requirements</h2>
                    <div className="prose prose-lg max-w-none text-muted-foreground whitespace-pre-wrap">
                      {job.requirements}
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <div className="bg-white rounded-2xl p-6 border-2 border-border">
                  <h3 className="font-bold mb-4">Job Details</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Category</p>
                      <p className="font-medium">{job.category}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Job Type</p>
                      <p className="font-medium capitalize">{job.job_type.replace('-', ' ')}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Work Arrangement</p>
                      <p className="font-medium capitalize">{job.work_arrangement}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Location</p>
                      <p className="font-medium">{job.location || `${job.city}, ${job.country}`}</p>
                    </div>
                    {job.visa_sponsorship && (
                      <div className="flex items-center gap-2 text-accent">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Visa Sponsorship Available</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 border-2 border-border">
                  <h3 className="font-bold mb-4">Posted By</h3>
                  <p className="text-muted-foreground">{job.poster_name}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default JobDetail;
